import styled, { css } from "styled-components/macro";

import type { ThemeProps } from "models/styles/theme";
import type { SortableTableVariant } from "./SortableTable.model";

interface TableProps extends ThemeProps {
  $variant?: SortableTableVariant;
  $gridSizes: string[];
}

interface HeaderCellProps extends ThemeProps {
  $flexStartCellHeaders: boolean;
  $variant?: SortableTableVariant;
}

interface CellProps extends ThemeProps {
  $rowIndex: number;
  $centerContent: boolean;
  $variant?: SortableTableVariant;
}

interface TableRowProps extends ThemeProps {
  $link?: string;
  $highlightColor?: string;
}

interface NoResultsMessageProps extends ThemeProps {
  $cellCount: number;
}

const getGridTemplateColumnsCss = (gridSizes: string[]) => css`
  grid-template-columns: ${gridSizes.join(" ")};
`;

export const StyledHeaderCell = styled.div<HeaderCellProps>`
  display: flex;
  flex-direction: column;

  ${(props: HeaderCellProps) => {
    switch (props.$variant) {
      case "white":
        return css`
          padding: 0.75rem;
          align-items: flex-start;
        `;
      default:
        return css`
          padding: 0.5rem;
          background-color: ${({ theme }: ThemeProps) => theme.palette.neutral._950};
          align-items: ${({ $flexStartCellHeaders }: HeaderCellProps) => ($flexStartCellHeaders ? "flex-start" : "center")};
        `;
    }
  }}
`;

export const StyledCell = styled.div<CellProps>`
  display: flex;
  font-size: ${({ theme }: CellProps) => theme.font.size._16};
  align-items: center;
  justify-content: ${({ $centerContent }: CellProps) => ($centerContent ? "center" : "flex-start")};

  ${(props: CellProps) => {
    switch (props.$variant) {
      case "white":
        return css`
          padding: 0.75rem;
          border-top: 1px solid ${({ theme }: ThemeProps) => theme.palette.neutral._900};
        `;
      default:
        return css`
          padding: 0.5rem;
        `;
    }
  }}
`;

export const StyledNoResultsMessage = styled.div<NoResultsMessageProps>`
  padding: 0.5rem;
  text-align: center;
  grid-column: 1 / ${({ $cellCount }: NoResultsMessageProps) => $cellCount};
`;

export const StyledTable = styled.div<TableProps>`
  display: grid;
  ${({ $gridSizes }: TableProps) => getGridTemplateColumnsCss($gridSizes)};

  ${(props: TableProps) =>
    props.$variant === "white" &&
    css`
    border-radius: 0.5rem;
    border: solid 1px ${props.theme.palette.neutral._900};
  `}
`;

export const StyledTableRow = styled.div<TableRowProps>`
  display: contents;

  ${(props: TableRowProps) =>
    props.$highlightColor &&
    css`
      ${StyledCell} {
        background: ${props.$highlightColor};
      }
    `};

  &:hover {
    ${StyledCell} {
      background-color: ${({ theme }: TableRowProps) => theme.palette.neutral._999};
      cursor: ${({ $link }: TableRowProps) => ($link ? "pointer" : "default")};
    }
  }
`;
